import { useState } from 'react'
import {
  FilterBody,
  FilteractionObject,
  Pagination,
  SortOption,
  YesObject,
  usePagination
} from '@yes.technology/react-toolkit'

import { useQuery } from '@tanstack/react-query'
import fetchYesObjects, {
  FetchYesObjectsResponse
} from '../api/fetchYesObjects'
import { ProjectFields } from './type'

type YesObjectFilterBody = FilterBody & {
  skip_relations?: boolean
  skip_metadata?: boolean
  project_fields?: ProjectFields
}
import { fetchFilteraction } from 'filteraction/FilteractionResolver'
import fetchFilteractionFilterbody from 'filteraction/shared/api/fetchFilteractionFilterbody'

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

type UseFetchYesObjectsArgs = {
  initialPagination?: Partial<Pagination>
  initialSortOptions?: SortOption[]
  filterBody: Optional<YesObjectFilterBody, 'pagination' | 'orderby'>
  showLoading?: boolean
  filteractionUuid?: string
  lazy?: boolean
}

export const INITIAL_PAGINATION = { limit: 10, offset: 0 }

export const INITIAL_SORT_OPTIONS: SortOption[] = [
  {
    date_creation_row: 'desc'
  }
]

const emptyArr: YesObject[] = []

export default function useFetchYesObjects({
  initialPagination = INITIAL_PAGINATION,
  initialSortOptions = INITIAL_SORT_OPTIONS,
  filterBody,
  filteractionUuid,
  showLoading,
  lazy
}: UseFetchYesObjectsArgs) {
  const { pagination, setPage } = usePagination({ initialPagination })

  const [sortOptions, setSortOptions] =
    useState<SortOption[]>(initialSortOptions)

  const { data, error, isLoading, refetch } = useQuery<
    FetchYesObjectsResponse,
    Error
  >({
    queryKey: [
      'YesObjects',
      { filterBody, showLoading, pagination, sortOptions },
      filteractionUuid
    ],
    queryFn: async () => {
      let callingFilterBody: YesObjectFilterBody = {
        pagination,
        orderby: sortOptions,
        ...filterBody
      }

      if (filteractionUuid) {
        const filteraction = (await fetchFilteraction(
          filteractionUuid,
          showLoading
        )) as FilteractionObject

        callingFilterBody = await fetchFilteractionFilterbody({
          filteraction,
          pagination,
          orderby: sortOptions
        })

        callingFilterBody = {
          ...callingFilterBody,
          skip_metadata: filterBody.skip_metadata,
          skip_relations: filterBody.skip_relations,
          project_fields: filterBody.project_fields
        }
      }

      return fetchYesObjects({ filterBody: callingFilterBody, showLoading })
    },
    enabled: !lazy
  })

  return {
    yesObjects: data?.objects || emptyArr,
    isLoading,
    fetchYesObjects: refetch,
    pagination,
    error,
    totalObjects: data?.pagination?.total || 0,
    setPage,
    sortOptions,
    setSortOptions
  }
}
